import React, { useCallback, useEffect, useRef } from "react";
import { Modal, Skeleton, Spin, message } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { MdErrorOutline } from "react-icons/md";
import { AiFillCaretRight } from "react-icons/ai";
import { FaCheckCircle } from "react-icons/fa";
import { IoIosCloseCircle } from "react-icons/io";
import {
  getIntegratedExchanges,
  setSelectedPortfolioExchange,
} from "../../../redux/exchange/actions";
import {
  getTargetExchangePortfolio as getTargetPortfolio,
  quickPlaceOrder as placeOrder,
  resetFormState,
  setTargetExchange,
} from "../../../redux/minimal-trade/minimalTradeSlice";
import useSuggestedSignal from "../../../hooks/useSuggestedSignal";
import styles from "../../../styles/Components/mini-app/mini-trade/MiniAppTradeModal.module.css";
import useUserData from "../../../hooks/useUserData";

function MiniAppTradeModal({ open = false, closeModal, coin = "" }) {
  const dispatch = useDispatch();
  const controllerRef = useRef(null);

  const {
    exchangeState: {
      integratedData,
      integratedDataLoading,
      getIntegratedList,
      integratedDataError,
    },
    miniTradeForm: {
      targetExchange,
      targetPortfolio,
      targetPortfolioStatus,
      step,
      orderStatus,
      orderResponse,
      orderErrorMessage,
    },
  } = useSelector((state) => state);

  const exchanges = integratedData?.filter(
    (ex) => ex?.status?.toLowerCase() === "active",
  );

  const {
    signal,
    stopLoss,
    takeProfit,
    positionSize: suggestedPS,
  } = useSuggestedSignal({ coin, oppositeSignal: "ai-trade" });
  const { username, email } = useUserData();

  const prediction = signal?.toLowerCase();
  const positionSize = suggestedPS ? suggestedPS / 100 : 0;
  const sl_percent = stopLoss ? stopLoss / 100 : "";
  const tp_percent = takeProfit ? takeProfit / 100 : "";

  const coinData = targetPortfolio?.find((asset) => asset?.assetName === coin);

  const validBalance =
    parseFloat(coinData?.availableBalance) > 0 ||
    parseFloat(coinData?.stakingBalance) > 0;

  const isAssetInPortfolio = !!coinData && validBalance;

  const nativeAmount =
    (parseFloat(coinData?.stakingBalance) || 0) +
    parseFloat(coinData?.availableBalance);

  const budget = parseFloat(
    targetPortfolio?.find((i) => i?.assetName === "USDT")?.availableBalance ||
      0,
  );

  const canAiBuy =
    !!targetExchange &&
    targetPortfolioStatus === "success" &&
    !!budget &&
    budget > 0 &&
    !!prediction &&
    !!positionSize &&
    prediction !== "sell" &&
    orderStatus !== "loading";

  const canAiSell =
    !!targetExchange &&
    targetPortfolioStatus === "success" &&
    isAssetInPortfolio &&
    nativeAmount > 0 &&
    orderStatus !== "loading";

  const abortPreviousRequests = () => {
    if (controllerRef.current) {
      controllerRef.current.abort();
    }
  };

  const fetchIntegratedExchanges = useCallback(() => {
    if (!email) return;
    dispatch(getIntegratedExchanges(username, email));
  }, [dispatch, email, username]);

  useEffect(() => {
    if (open && email) {
      if (
        !integratedDataLoading &&
        !getIntegratedList &&
        !integratedDataError
      ) {
        fetchIntegratedExchanges();
      }
    }
  }, [
    open,
    email,
    integratedDataLoading,
    getIntegratedList,
    integratedDataError,
    fetchIntegratedExchanges,
  ]);

  useEffect(() => {
    if (open) {
      dispatch(resetFormState());
    }
  }, [open, dispatch]);

  const selectFirstExchange = useCallback(() => {
    const firstExchangeId = exchanges?.[0]?.id;
    if (!firstExchangeId) return;

    if (targetPortfolioStatus === "loading") {
      abortPreviousRequests();
    }

    controllerRef.current = new AbortController();
    const { signal: abortSignal } = controllerRef.current;

    dispatch(setTargetExchange(firstExchangeId));
    dispatch(getTargetPortfolio(username, email, firstExchangeId, abortSignal));
  }, [dispatch, exchanges, email, username, targetPortfolioStatus]);

  const handleExchangeSelection = useCallback(
    (exchange) => {
      if (!exchange || targetExchange === exchange.id) return;

      if (targetPortfolioStatus === "loading") {
        abortPreviousRequests();
      }

      controllerRef.current = new AbortController();
      const { signal: abortSignal } = controllerRef.current;

      dispatch(setTargetExchange(exchange.id));
      dispatch(getTargetPortfolio(username, email, exchange.id, abortSignal));
    },
    [dispatch, targetExchange, targetPortfolioStatus, email, username],
  );

  useEffect(() => {
    if (open && !targetExchange && exchanges?.length > 0) {
      selectFirstExchange();
    }
  }, [open, targetExchange, exchanges, selectFirstExchange]);

  useEffect(() => {
    if (targetPortfolioStatus === "loading") {
      return () => abortPreviousRequests();
    }
  }, [targetPortfolioStatus]);

  const updatePortfolio = () => {
    dispatch(setSelectedPortfolioExchange(targetExchange, username, email));
  };

  const handleAiBuy = () => {
    if (!canAiBuy) {
      if (!budget || budget <= 0) {
        message.info("Insufficient USDT balance to buy.");
      } else if (!prediction || prediction === "sell") {
        message.info("AI buy suggestion not available for this asset.");
      }
      return;
    }

    dispatch(
      placeOrder(
        username,
        email,
        "buy_with_oco",
        targetExchange,
        coin,
        positionSize * budget,
        sl_percent,
        tp_percent,
      ),
    );
  };

  const handleAiSell = () => {
    if (!canAiSell) {
      if (!isAssetInPortfolio || nativeAmount <= 0) {
        message.info("This asset is not available in your portfolio to sell.");
      }
      return;
    }

    dispatch(
      placeOrder(
        username,
        email,
        "sell",
        targetExchange,
        coin,
        nativeAmount,
        "",
      ),
    );
  };

  const handleClose = () => {
    if (orderStatus === "loading") {
      message.info("Please wait for the order to finish processing.");
      return;
    }
    dispatch(resetFormState());
    closeModal?.();
  };

  const renderSectionLabel = (label) => (
    <div className={styles.sectionLabel}>
      {/*<AiFillCaretRight />*/}
      {label}
    </div>
  );

  const renderExchangeSection = () => {
    if (integratedDataLoading) {
      return (
        <div className={styles.loader}>
          <Skeleton.Node active />
          <div className={styles.spinner}>
            <Spin size="small" />
            <p>Loading integrated exchanges...</p>
          </div>
        </div>
      );
    }

    if (integratedDataError) {
      return (
        <div className={styles.errorMessage}>
          <MdErrorOutline size={16} />
          <span>
            Failed to fetch integrated exchange list!{" "}
            <span
              className={styles.retryBtn}
              onClick={fetchIntegratedExchanges}
            >
              Retry
            </span>
          </span>
        </div>
      );
    }

    if (!exchanges || exchanges.length === 0) {
      return (
        <div className={styles.emptyState}>
          <div className={styles.emptyStateIcon}>
            <MdErrorOutline size={32} />
          </div>
          <h4 className={styles.emptyStateTitle}>No Exchange Connected</h4>
          <p className={styles.emptyStateText}>
            You don't have any exchange connected to your account yet.
          </p>
          <p className={styles.emptyStateSubtext}>
            Please use the Desktop App to connect to different exchanges and
            start trading.
          </p>
        </div>
      );
    }

    return (
      <ul className={styles.exchangeList}>
        {exchanges?.map((ex) => (
          <li
            key={ex?.id}
            className={`${styles.exchange} ${
              targetExchange === ex?.id ? styles.selectedExchange : ""
            }`}
            onClick={() => handleExchangeSelection(ex)}
          >
            <LazyLoadImage
              src={`https://cryptocrispypro.s3.ap-southeast-2.amazonaws.com/crypto_exchanges_logo/${ex?.id}.png`}
              alt={ex?.id}
              wrapperClassName={styles.exchangeIcon}
              effect="blur"
            />
            <span className={styles.exchangeName}>{ex?.name}</span>
          </li>
        ))}
      </ul>
    );
  };

  const renderPortfolioStatus = () => {
    if (targetPortfolioStatus === "loading") {
      return (
        <div className={styles.statusMessage}>
          <Spin size="small" />
          <span>Fetching portfolio...</span>
        </div>
      );
    }

    if (targetPortfolioStatus === "error") {
      return (
        <div className={styles.statusError}>
          <MdErrorOutline size={16} />
          <span>Failed to load portfolio.</span>
        </div>
      );
    }

    return null;
  };

  const renderOrderResult = () => {
    if (orderStatus === "loading") {
      return (
        <div className={styles.resultBox}>
          <div className={styles.loadingStatus}>
            <Spin size="large" />
            <p>Processing your order...</p>
          </div>
        </div>
      );
    }

    const isSuccess =
      orderResponse?.status_code === 200 || orderStatus === "success";

    if (isSuccess) {
      return (
        <div className={styles.resultBox}>
          <div className={styles.resultMessage}>
            <FaCheckCircle size={36} color="#2ecc71" />
            <h3>Order Placed Successfully</h3>
            <p>Your trade has been executed successfully.</p>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.resultBox}>
        <div className={styles.resultMessage}>
          <IoIosCloseCircle size={40} color="#e74c3c" />
          <h3>Order Failed</h3>
          <p>{orderErrorMessage?.error || "Something went wrong."}</p>
          {orderErrorMessage?.description && (
            <p className={styles.resultSubtext}>
              {orderErrorMessage?.description}
            </p>
          )}
        </div>
      </div>
    );
  };

  return (
    <Modal
      centered
      footer={null}
      open={open}
      onCancel={handleClose}
      maskClosable={false}
      closable={orderStatus !== "loading"}
      className={styles.modalContainer}
      width={480}
    >
      <div className={styles.modalContent}>
        {step === 1 && (
          <>
            {renderSectionLabel("Exchange")}
            {renderExchangeSection()}

            {(exchanges?.length > 0 || targetPortfolioStatus) && (
              <div className={styles.actionsSection}>
                {renderPortfolioStatus()}

                <div className={styles.actionButtons}>
                  <button
                    type="button"
                    className={`${styles.actionBtn} ${styles.buyBtn} ${
                      !canAiBuy ? styles.disabled : ""
                    }`}
                    onClick={handleAiBuy}
                    // disabled={!canAiBuy}
                  >
                    AI Buy
                  </button>
                  <button
                    type="button"
                    className={`${styles.actionBtn} ${styles.sellBtn} ${
                      !canAiSell ? styles.disabled : ""
                    }`}
                    onClick={handleAiSell}
                    // disabled={!canAiSell}
                  >
                    AI Sell
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {step === 2 && renderOrderResult()}
      </div>
    </Modal>
  );
}

export default MiniAppTradeModal;
